import { ImgHTMLAttributes, useState } from 'react';
import { cn } from '../../lib/cn';

/**
 * Image — wrapper sobre `<img>` con buenas defaults:
 *  - loading=lazy por defecto (eager si priority=true).
 *  - decoding=async.
 *  - width/height obligatorios para evitar CLS.
 *  - blur placeholder opcional via `blurDataUrl`.
 *  - fallback (skeleton crema) mientras carga / si hay error.
 *
 * No usa hex literals. Las clases dependen exclusivamente de los tokens.
 */
export type ImageRatio = '1:1' | '4:5' | '16:9' | '21:9' | 'auto';
export type ImageFit = 'cover' | 'contain';

export interface ImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'loading' | 'width' | 'height'> {
  src: string;
  alt: string;
  width: number;
  height: number;
  /** Ratio de aspecto del contenedor. Si `auto`, conserva proporciones intrinsecas. */
  ratio?: ImageRatio;
  fit?: ImageFit;
  /** Si true, fuerza loading=eager + fetchpriority=high (uso: hero LCP). */
  priority?: boolean;
  /** Placeholder blurred mientras carga (data URI o blurhash decodificado). */
  blurDataUrl?: string;
  className?: string;
}

const ratioClasses: Record<ImageRatio, string> = {
  '1:1': 'aspect-square',
  '4:5': 'aspect-[4/5]',
  '16:9': 'aspect-video',
  '21:9': 'aspect-[21/9]',
  auto: '',
};

export function Image({
  src,
  alt,
  width,
  height,
  ratio = 'auto',
  fit = 'cover',
  priority = false,
  blurDataUrl,
  className,
  ...rest
}: ImageProps) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  return (
    <div
      className={cn(
        'relative overflow-hidden bg-cream-200',
        ratioClasses[ratio],
        className,
      )}
      style={ratio === 'auto' ? { aspectRatio: `${width} / ${height}` } : undefined}
    >
      {blurDataUrl && !loaded && !errored && (
        // Blur placeholder estatico debajo de la imagen real.
        <img
          src={blurDataUrl}
          alt=""
          aria-hidden="true"
          width={width}
          height={height}
          className={cn(
            'absolute inset-0 w-full h-full',
            fit === 'cover' ? 'object-cover' : 'object-contain',
            'scale-105 blur-md',
          )}
        />
      )}

      {!errored && (
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          // fetchpriority es HTML estandar; React lo soporta como atributo string.
          {...(priority ? { fetchpriority: 'high' as const } : {})}
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          className={cn(
            'absolute inset-0 w-full h-full transition-opacity duration-2 ease-out',
            fit === 'cover' ? 'object-cover' : 'object-contain',
            loaded ? 'opacity-100' : 'opacity-0',
          )}
          {...rest}
        />
      )}

      {errored && (
        // Fallback simple: tono crema sin texto visible (alt sirve a SR).
        <div
          role="img"
          aria-label={alt}
          className="absolute inset-0 flex items-center justify-center bg-cream-200 text-mute-500 text-caption"
        >
          <span aria-hidden="true">—</span>
        </div>
      )}
    </div>
  );
}
